/**
 * オフラインの保存と同期（Web・アプリ共通）。約束事は ../CONTRACT.md、設計は docs/offline-sync.md。
 * 保存先は LocalStore を差し替える（Web は IndexedDB、アプリは SQLite、テストは MemoryStore）。
 */
import {
  type Customer,
  Customer as CustomerSchema,
  type EstimateRules,
  Estimate as EstimateSchema,
  type Maker,
  type Pattern,
  type Process,
  type Product,
  type Proposal,
  Proposal as ProposalSchema,
  type Revision,
  type SyncOp,
  SyncPullResponse,
  SyncPushResponse,
} from "@el/schema";

export type SyncPullResponseT = ReturnType<typeof SyncPullResponse.parse>;
export type SyncOpResultT = ReturnType<typeof SyncPushResponse.parse>["results"][number];

export type MetaKey = "serverSeq" | "lastSyncAt" | "userId";

export interface LocalStore {
  getMeta(key: MetaKey): Promise<string | null>;
  setMeta(key: string, value: string): Promise<void>;
  /**
   * 1 トランザクションで反映し、同じトランザクションで meta の serverSeq に res.serverSeq を書く。
   * wipe なら meta も送信待ちも含めて全部消す。送信待ちのある顧客・提案・版は、端末の値を残す
   */
  applyPull(res: SyncPullResponseT): Promise<void>;
  enqueue(op: SyncOp): Promise<void>;
  /** 古い順 */
  peekOutbox(limit: number): Promise<SyncOp[]>;
  removeFromOutbox(opIds: string[]): Promise<void>;
  markOutboxError(opId: string, message: string): Promise<void>;
  outboxCount(): Promise<number>;
  /** 行を書くだけ（送信待ちには入れない。SyncEngine が enqueue と組で呼ぶ） */
  upsertCustomer(c: Customer): Promise<void>;
  upsertProposal(p: Proposal): Promise<void>;
  /** 同じ ID があれば置き換える */
  addRevision(r: Revision): Promise<void>;
  /** 版を消す（サーバーが受け付けなかった版を端末から消すときだけ使う。無ければ何もしない） */
  deleteRevision(id: string): Promise<void>;
  clearAll(): Promise<void>;
  /** fn の中の書き込みを1つのトランザクションにする。例外なら全部取り消す。入れ子は外側にまとめる */
  transaction(fn: () => Promise<void>): Promise<void>;
}

export type SyncState = "synced" | "offline" | "syncing" | "error";
export type SyncStatus = { state: SyncState; pending: number; lastSyncAt: string | null; message?: string };

/** 画面に出す文言（普通の業務サービスの敬体。技術用語を出さない） */
export const SYNC_MESSAGES = {
  relogin: "ログインし直してください",
  wiped: "このアカウントは利用できなくなったため、この端末のデータを削除しました。管理者にお問い合わせください。",
  retryLater: "同期できませんでした。しばらくしてから自動でもう一度送ります。",
  badResponse: "同期できませんでした。しばらくしてからもう一度お試しください。",
  rejected: (n: number) => `保存できなかった変更が ${n} 件あります。`,
  notApplied: "一部の変更がまだ送れていません。次の同期でもう一度送ります。",
} as const;

export const PUSH_BATCH = 500;
export const PULL_LIMIT = 500;
const DAY_MS = 86_400_000;
/** 端末の時計が最後の同期より前に戻っていても、この幅までは許す */
const CLOCK_SKEW_MS = 5 * 60 * 1000;
const MAX_PUSH_ROUNDS = 50;
const MAX_PULL_PAGES = 10_000;

export type SyncEngineOptions = {
  store: LocalStore;
  baseUrl: string;
  fetch: typeof fetch;
  auth: () => Promise<Record<string, string>>;
  deviceId: string;
  now: () => string;
  idFactory: () => string;
  /** 送信の結果ごとに呼ぶ（枝分かれ・項目の衝突・見積の再計算の差を画面に出すため。任意） */
  onServerResult?: (r: SyncOpResultT) => void;
};

class StopSync extends Error {
  constructor(public status: Omit<SyncStatus, "pending" | "lastSyncAt">) {
    super(status.message ?? status.state);
  }
}

type ErrorBody = { error?: { code?: string; message?: string } };

export class SyncEngine {
  private listeners = new Set<(s: SyncStatus) => void>();
  private status: SyncStatus = { state: "synced", pending: 0, lastSyncAt: null };
  private running: Promise<SyncStatus> | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private detach: (() => void) | null = null;

  constructor(private opts: SyncEngineOptions) {}

  /* ===== 端末での変更 ===== */

  /** 端末に保存して送信待ちへ。updatedAt はサーバーから受け取った値のまま渡す（変更前の値の判定に使う） */
  async saveCustomer(c: Customer): Promise<void> {
    await this.write(() => this.opts.store.upsertCustomer(c), {
      opId: this.opts.idFactory(),
      entity: "customer",
      action: c.deletedAt ? "delete" : "upsert",
      data: c,
      baseUpdatedAt: c.updatedAt,
      clientTs: this.opts.now(),
    });
  }

  async saveProposal(p: Proposal, baseUpdatedAt: string | null): Promise<void> {
    await this.write(() => this.opts.store.upsertProposal(p), {
      opId: this.opts.idFactory(),
      entity: "proposal",
      action: p.deletedAt ? "delete" : "upsert",
      data: p,
      baseUpdatedAt,
      clientTs: this.opts.now(),
    });
  }

  /** 版は追記だけ。番号（label）はサーバーが決めるので、端末では null のまま */
  async addRevision(r: Revision): Promise<void> {
    const rev = { ...r, label: null };
    await this.write(() => this.opts.store.addRevision(rev), { opId: this.opts.idFactory(), entity: "revision", action: "create", data: rev, baseUpdatedAt: null, clientTs: this.opts.now() });
  }

  /** 行の保存と送信待ちへの追加を同じトランザクションで */
  private async write(save: () => Promise<void>, op: SyncOp) {
    const { store } = this.opts;
    await store.transaction(async () => {
      await save();
      await store.enqueue(op);
    });
    await this.emit({ ...this.status, pending: await this.opts.store.outboxCount() });
  }

  /* ===== 同期 ===== */

  /** push（500 件ずつ）→ pull（hasMore の間）。同時に呼ばれたら同じ処理を待つ */
  syncNow(): Promise<SyncStatus> {
    if (!this.running) {
      this.running = this.run().finally(() => {
        this.running = null;
      });
    }
    return this.running;
  }

  private async run(): Promise<SyncStatus> {
    const { store } = this.opts;
    const lastSyncAt = await store.getMeta("lastSyncAt");
    await this.emit({ state: "syncing", pending: await store.outboxCount(), lastSyncAt });
    let rejected = 0;
    let notApplied = false;
    try {
      const headers = await this.opts.auth();
      const pushed = await this.push(headers);
      rejected = pushed.rejected;
      notApplied = pushed.notApplied;
      await this.pull(headers);
      await store.setMeta("lastSyncAt", this.opts.now());
    } catch (e) {
      const pending = await store.outboxCount();
      const last = await store.getMeta("lastSyncAt");
      if (e instanceof StopSync) return this.emit({ ...e.status, pending, lastSyncAt: last });
      return this.emit({ state: "error", message: SYNC_MESSAGES.badResponse, pending, lastSyncAt: last });
    }
    const pending = await store.outboxCount();
    const last = await store.getMeta("lastSyncAt");
    if (rejected > 0) return this.emit({ state: "error", message: SYNC_MESSAGES.rejected(rejected), pending, lastSyncAt: last });
    if (notApplied) return this.emit({ state: "error", message: SYNC_MESSAGES.notApplied, pending, lastSyncAt: last });
    return this.emit({ state: "synced", pending, lastSyncAt: last });
  }

  private async request(path: string, init: RequestInit, headers: Record<string, string>): Promise<unknown> {
    let res: Response;
    try {
      res = await this.opts.fetch(`${this.opts.baseUrl.replace(/\/$/, "")}${path}`, { ...init, headers: { ...headers, ...(init.headers as Record<string, string> | undefined) } });
    } catch {
      throw new StopSync({ state: "offline" });
    }
    if (res.ok) {
      try {
        return await res.json();
      } catch {
        throw new StopSync({ state: "error", message: SYNC_MESSAGES.badResponse });
      }
    }
    let body: ErrorBody = {};
    try {
      body = (await res.json()) as ErrorBody;
    } catch {
      body = {};
    }
    if (res.status === 401) {
      if (body.error?.code === "account_disabled") await this.wipe();
      throw new StopSync({ state: "error", message: body.error?.code === "account_disabled" ? SYNC_MESSAGES.wiped : SYNC_MESSAGES.relogin });
    }
    if (res.status === 403 && body.error?.code === "account_disabled") {
      await this.wipe();
      throw new StopSync({ state: "error", message: SYNC_MESSAGES.wiped });
    }
    // 通信・5xx・429 は送信待ちに残して、次に送り直す
    throw new StopSync({ state: "error", message: SYNC_MESSAGES.retryLater });
  }

  private async wipe() {
    await this.opts.store.clearAll();
  }

  private async push(headers: Record<string, string>): Promise<{ rejected: number; notApplied: boolean }> {
    const { store } = this.opts;
    let rejected = 0;
    let notApplied = false;
    for (let round = 0; round < MAX_PUSH_ROUNDS; round++) {
      const ops = await store.peekOutbox(PUSH_BATCH);
      if (ops.length === 0) break;
      const json = await this.request("/api/sync/push", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ deviceId: this.opts.deviceId, ops }) }, headers);
      const parsed = SyncPushResponse.safeParse(json);
      if (!parsed.success) throw new StopSync({ state: "error", message: SYNC_MESSAGES.badResponse });
      const byId = new Map(parsed.data.results.map((r) => [r.opId, r]));
      const done: string[] = [];
      const handled: SyncOpResultT[] = [];
      await store.transaction(async () => {
        for (const op of ops) {
          const r = byId.get(op.opId);
          if (!r) {
            // 結果が返らなかった操作は残して次に送る（一部だけ反映されたとき）
            notApplied = true;
            await store.markOutboxError(op.opId, SYNC_MESSAGES.notApplied);
            continue;
          }
          if (r.result === "rejected") {
            rejected++;
            if (op.entity === "revision") await store.deleteRevision(op.data.id);
            else await this.restoreServerCopy(op, r);
          } else if (op.entity === "revision") {
            await this.applyRevisionResult(op.data, r);
          }
          done.push(op.opId);
          handled.push(r);
        }
        if (done.length > 0) await store.removeFromOutbox(done);
      });
      for (const r of handled) this.opts.onServerResult?.(r);
      if (done.length < ops.length) break; // 進まない分はこの回では送り直さない
    }
    return { rejected, notApplied };
  }

  /** 版の番号とサーバーで計算し直した見積を、端末の版にすぐ書く（取得を待たずに表示を合わせる） */
  private async applyRevisionResult(rev: Revision, r: SyncOpResultT) {
    const sd = r.serverData as { label?: unknown; estimate?: unknown } | undefined;
    if (!sd || typeof sd.label !== "string") return;
    const est = EstimateSchema.safeParse(sd.estimate);
    await this.opts.store.addRevision({ ...rev, label: sd.label, ...(est.success ? { estimate: est.data } : {}) });
  }

  /** 受け付けられなかった変更は、サーバーの値が返っていれば端末の表示をそれに戻す */
  private async restoreServerCopy(op: SyncOp, r: SyncOpResultT) {
    const current = (r.serverData as { current?: unknown } | undefined)?.current;
    if (!current) return;
    if (op.entity === "customer") {
      const c = CustomerSchema.safeParse(current);
      if (c.success) await this.opts.store.upsertCustomer(c.data);
    } else if (op.entity === "proposal") {
      const p = ProposalSchema.safeParse(current);
      if (p.success) await this.opts.store.upsertProposal(p.data);
    }
  }

  private async pull(headers: Record<string, string>): Promise<void> {
    const { store } = this.opts;
    for (let page = 0; page < MAX_PULL_PAGES; page++) {
      const since = (await store.getMeta("serverSeq")) ?? "0";
      const json = await this.request(`/api/sync/pull?since=${encodeURIComponent(since)}&limit=${PULL_LIMIT}`, { method: "GET" }, headers);
      const parsed = SyncPullResponse.safeParse(json);
      if (!parsed.success) throw new StopSync({ state: "error", message: SYNC_MESSAGES.badResponse });
      const res = parsed.data;
      await store.applyPull(res); // serverSeq も store が同じトランザクションで書く
      if (res.wipe) throw new StopSync({ state: "error", message: SYNC_MESSAGES.wiped });
      if (!res.hasMore) return;
    }
  }

  /** つながったとき・30 秒ごと・画面が前に来たときに同期する */
  start(intervalMs = 30_000): void {
    if (this.timer) return;
    this.timer = setInterval(() => void this.syncNow(), intervalMs);
    const g = globalThis as {
      addEventListener?: (t: string, h: () => void) => void;
      removeEventListener?: (t: string, h: () => void) => void;
      document?: { visibilityState?: string; addEventListener?: (t: string, h: () => void) => void; removeEventListener?: (t: string, h: () => void) => void };
    };
    const onOnline = () => void this.syncNow();
    const onVisible = () => {
      if (g.document?.visibilityState === "visible") void this.syncNow();
    };
    g.addEventListener?.("online", onOnline);
    g.document?.addEventListener?.("visibilitychange", onVisible);
    this.detach = () => {
      g.removeEventListener?.("online", onOnline);
      g.document?.removeEventListener?.("visibilitychange", onVisible);
    };
    void this.syncNow();
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.detach?.();
    this.detach = null;
  }

  /** 状態が変わるたびに呼ぶ。登録した時点の状態もすぐ渡す */
  onStatus(cb: (s: SyncStatus) => void): () => void {
    this.listeners.add(cb);
    cb(this.status);
    return () => {
      this.listeners.delete(cb);
    };
  }

  getStatus(): SyncStatus {
    return this.status;
  }

  private async emit(s: SyncStatus): Promise<SyncStatus> {
    this.status = s;
    for (const cb of this.listeners) cb(s);
    return s;
  }

  /** 最後の同期から graceDays 未満ならオフラインで使える（ちょうどで使えなくなる）。時計が大きく戻っていたら使わせない */
  async offlineAllowed(graceDays: number): Promise<boolean> {
    const last = await this.opts.store.getMeta("lastSyncAt");
    if (!last) return false;
    const lastMs = Date.parse(last);
    const nowMs = Date.parse(this.opts.now());
    if (!Number.isFinite(lastMs) || !Number.isFinite(nowMs)) return false;
    const elapsed = nowMs - lastMs;
    if (elapsed < -CLOCK_SKEW_MS) return false;
    return elapsed < graceDays * DAY_MS;
  }
}

/* ===== テスト用・試作用の保存先 ===== */

type OutboxEntry = { op: SyncOp; error: string | null };

export class MemoryStore implements LocalStore {
  meta = new Map<string, string>();
  makers = new Map<string, Maker>();
  processes = new Map<string, Process>();
  products = new Map<string, Product>();
  patterns = new Map<string, Pattern>();
  estimateRules = new Map<string, EstimateRules>();
  customers = new Map<string, Customer>();
  proposals = new Map<string, Proposal>();
  revisions = new Map<string, Revision>();
  outbox: OutboxEntry[] = [];
  /** applyPull の途中で失敗させる（テスト用） */
  failNextApply: Error | null = null;

  async getMeta(key: MetaKey): Promise<string | null> {
    return this.meta.get(key) ?? null;
  }
  async setMeta(key: string, value: string): Promise<void> {
    this.meta.set(key, value);
  }

  private pendingIds(entity: SyncOp["entity"]): Set<string> {
    return new Set(this.outbox.filter((e) => e.op.entity === entity).map((e) => e.op.data.id));
  }

  async applyPull(res: SyncPullResponseT): Promise<void> {
    if (this.failNextApply) {
      const e = this.failNextApply;
      this.failNextApply = null;
      throw e;
    }
    if (res.wipe) {
      await this.clearAll();
      return;
    }
    // 全部うまくいってから入れ替える（1 トランザクション相当）。serverSeq も一緒に書く
    const next = {
      makers: new Map(this.makers),
      processes: new Map(this.processes),
      products: new Map(this.products),
      patterns: new Map(this.patterns),
      estimateRules: new Map(this.estimateRules),
      customers: new Map(this.customers),
      proposals: new Map(this.proposals),
      revisions: new Map(this.revisions),
    };
    for (const x of res.makers) next.makers.set(x.id, x);
    for (const x of res.processes) next.processes.set(x.id, x);
    for (const x of res.products) next.products.set(x.id, x);
    for (const x of res.patterns) next.patterns.set(x.id, x);
    for (const x of res.estimateRules) next.estimateRules.set(x.id, x);
    const pc = this.pendingIds("customer");
    const pp = this.pendingIds("proposal");
    const pr = this.pendingIds("revision");
    for (const x of res.customers) if (!pc.has(x.id)) next.customers.set(x.id, x);
    for (const x of res.proposals) if (!pp.has(x.id)) next.proposals.set(x.id, x);
    for (const x of res.revisions) if (!pr.has(x.id)) next.revisions.set(x.id, x);
    const tables: Record<string, Map<string, unknown>> = {
      maker: next.makers,
      process: next.processes,
      product: next.products,
      pattern: next.patterns,
      estimateRules: next.estimateRules,
    };
    for (const d of res.deleted) tables[d.entity]?.delete(d.id);
    Object.assign(this, next);
    this.meta.set("serverSeq", String(res.serverSeq));
  }

  private depth = 0;
  async transaction(fn: () => Promise<void>): Promise<void> {
    if (this.depth > 0) return fn(); // 入れ子は外側にまとめる
    const saved = this.snapshot();
    this.depth++;
    try {
      await fn();
    } catch (e) {
      Object.assign(this, saved);
      throw e;
    } finally {
      this.depth--;
    }
  }

  private snapshot() {
    return {
      meta: new Map(this.meta),
      makers: new Map(this.makers),
      processes: new Map(this.processes),
      products: new Map(this.products),
      patterns: new Map(this.patterns),
      estimateRules: new Map(this.estimateRules),
      customers: new Map(this.customers),
      proposals: new Map(this.proposals),
      revisions: new Map(this.revisions),
      outbox: this.outbox.map((e) => ({ ...e })),
    };
  }

  async enqueue(op: SyncOp): Promise<void> {
    this.outbox.push({ op, error: null });
  }
  async peekOutbox(limit: number): Promise<SyncOp[]> {
    return this.outbox.slice(0, limit).map((e) => e.op);
  }
  async removeFromOutbox(opIds: string[]): Promise<void> {
    const ids = new Set(opIds);
    this.outbox = this.outbox.filter((e) => !ids.has(e.op.opId));
  }
  async markOutboxError(opId: string, message: string): Promise<void> {
    for (const e of this.outbox) if (e.op.opId === opId) e.error = message;
  }
  async outboxCount(): Promise<number> {
    return this.outbox.length;
  }
  async upsertCustomer(c: Customer): Promise<void> {
    this.customers.set(c.id, c);
  }
  async upsertProposal(p: Proposal): Promise<void> {
    this.proposals.set(p.id, p);
  }
  async addRevision(r: Revision): Promise<void> {
    this.revisions.set(r.id, r);
  }
  async deleteRevision(id: string): Promise<void> {
    this.revisions.delete(id);
  }
  async clearAll(): Promise<void> {
    this.meta.clear();
    for (const m of [this.makers, this.processes, this.products, this.patterns, this.estimateRules, this.customers, this.proposals, this.revisions]) m.clear();
    this.outbox = [];
  }
}
